import React, { useState, useEffect } from 'react';
import { Breakpoints } from "@pb/utils/constants/breakpoints";
import { default as useMediaQuery } from "@pb/utils/reactHooks/useMediaQuery";
import cn from 'classnames';
import ScreenReaderOnlyText from "../../../helpers/ScreenReaderOnlyText";
import ListGrid from "../../ListGrid";
import styles from './SizeButtonGroup.module.scss';
export const SizeButtonGroup = ({ variants, sizeButtons, setSelectedSizeCode, isComingSoon, labels, fewPieceLeftIconPosition, outOfStockStyling, isCentered, sizeSelectorWidth, }) => {
    const [buttonsPerRow, setButtonsPerRow] = useState(0);
    const isMobile = useMediaQuery(Breakpoints.SMALL);
    const handleSizeSelect = (code) => {
        setSelectedSizeCode(code);
    };
    /*   HM: sizeButtons = large
    COS: sizeButtons = small
    Other stories: sizeButtons = xsmall
   */
    const isLargeButtons = sizeButtons === 'large';
    const isSmallButtons = sizeButtons === 'small';
    const isXsmallButtons = sizeButtons === 'xsmall';
    const fewPiecesLeftStyle = () => {
        switch (fewPieceLeftIconPosition) {
            case 'center':
                return styles.center;
            case 'top':
                return styles.topCorner;
            case 'bottom-corner':
                return styles.bottomCorner;
            default:
                return styles.center;
        }
    };
    const outOfStockStyle = () => {
        if (sizeButtons === 'large') {
            return outOfStockStyling === 'line-through'
                ? styles.isOutOfStockLine
                : styles.isOutOfStockGreyedOut;
        }
        switch (outOfStockStyling) {
            case 'greyed-out':
                return styles.isOutOfStockGreyedOut;
            case 'crossed-over':
                return styles.isOutOfStockCrossedOver;
            default:
                return styles.isOutOfStockGreyedOut;
        }
    };
    const gridStyle = () => {
        switch (sizeButtons) {
            case 'large':
                return styles.gridLargeButtons;
            case 'small':
                return styles.gridSmallButtons;
            case 'xsmall':
                return styles.gridXSmallButtons;
            default:
                return styles.gridLargeButtons;
        }
    };
    const getSizeButtons = () => {
        switch (sizeButtons) {
            case 'large':
                return styles.lButton;
            case 'small':
                return styles.sButton;
            case 'xsmall':
                return styles.xsButton;
            default:
                return styles.lButton;
        }
    };
    const createSizeNameSpan = () => {
        const span = document.createElement('span');
        span.style.visibility = 'hidden';
        document.body.appendChild(span);
        return span;
    };
    const calculateSizeCellWidth = () => {
        var _a, _b;
        const lateralPadding = 24;
        const getLongestSizeName = variants && ((_a = [...variants]) === null || _a === void 0 ? void 0 : _a.sort((a, b) => b.sizeName.length - a.sizeName.length)[0]);
        const longestSizeNameIndex = variants === null || variants === void 0 ? void 0 : variants.indexOf(getLongestSizeName);
        const longestSizeName = (_b = variants[longestSizeNameIndex]) === null || _b === void 0 ? void 0 : _b.sizeName;
        // Buttons width
        const sizeNameSpan = createSizeNameSpan();
        sizeNameSpan.innerText = longestSizeName;
        const sizeNameWidth = Math.round(sizeNameSpan.getBoundingClientRect().width);
        sizeNameSpan.remove();
        const minWidthPerButton = sizeNameWidth + lateralPadding * 2;
        // Buttons row
        const numberOfButtonsPerRow = sizeSelectorWidth && minWidthPerButton && Math.floor(sizeSelectorWidth / minWidthPerButton);
        const actualNumberOfButtonsPerRow = numberOfButtonsPerRow && numberOfButtonsPerRow > 5 ? 5 : numberOfButtonsPerRow;
        if (actualNumberOfButtonsPerRow) {
            setButtonsPerRow(actualNumberOfButtonsPerRow);
        }
    };
    useEffect(() => {
        if (sizeSelectorWidth && variants) {
            calculateSizeCellWidth();
        }
    }, [sizeSelectorWidth, variants]);
    const columnStyle = () => {
        const actualButtonsPerRow = variants.length >= 2 ? buttonsPerRow : 1;
        return {
            xsmall: actualButtonsPerRow,
            small: actualButtonsPerRow,
            smallPlus: actualButtonsPerRow,
            medium: actualButtonsPerRow,
            large: actualButtonsPerRow,
            xlarge: actualButtonsPerRow,
            xxl: actualButtonsPerRow,
        };
    };
    return (React.createElement(ListGrid, { className: cn(isCentered ? styles.flexCentered : gridStyle()), numberOfColumns: isLargeButtons ? columnStyle() : undefined, shouldNotHaveGridStyle: isSmallButtons || isXsmallButtons }, variants === null || variants === void 0 ? void 0 : variants.map((variant) => {
        const { sizeCode, sizeName, inStock, hasFewPiecesLeft, isSelected } = variant;
        if (isLargeButtons && isComingSoon) {
            return (React.createElement("label", { htmlFor: sizeName, "data-testid": "pdp_fav_size_selector_button", className: cn(styles.btn, getSizeButtons(), styles.comingSoon, styles.sizeList, 'sizeItem'), "aria-disabled": true, key: sizeName, "data-code": variant.code },
                React.createElement("input", { type: "radio", id: sizeName, name: "sizeLabel", className: styles.buttonInput, disabled: true }),
                sizeName,
                React.createElement(ScreenReaderOnlyText, null, labels.labelComingSoon)));
        }
        if (hasFewPiecesLeft) {
            return (React.createElement("label", { htmlFor: sizeName, key: sizeName, "data-testid": "pdp_fav_size_selector_button", "data-code": variant.code, className: cn(styles.btn, styles.sizeList, getSizeButtons(), styles.isFewPiecesLeft, fewPiecesLeftStyle(), fewPieceLeftIconPosition === 'bottom-corner' && styles.btnOverflowHidden, isSelected && !isLargeButtons
                    ? styles.selectedUnderline
                    : isSelected && isLargeButtons
                        ? styles.selectedFilled
                        : '', 'sizeItem') },
                React.createElement("input", { type: "radio", id: sizeName, name: "sizeLabel", className: styles.buttonInput, onChange: () => sizeCode && handleSizeSelect(sizeCode) }),
                sizeName,
                React.createElement(ScreenReaderOnlyText, null, labels.labelFewPiecesLeft)));
        }
        if (inStock) {
            return (React.createElement("label", { htmlFor: sizeName, key: sizeName, "data-testid": "pdp_fav_size_selector_button", "data-code": variant.code, className: cn(styles.btn, styles.sizeList, getSizeButtons(), isSelected && !isLargeButtons
                    ? styles.selectedUnderline
                    : isSelected && isLargeButtons
                        ? styles.selectedFilled
                        : '', 'sizeItem') },
                React.createElement("input", { type: "radio", id: sizeName, name: "sizeLabel", className: styles.buttonInput, onChange: () => sizeCode && handleSizeSelect(sizeCode) }),
                sizeName));
        }
        return (React.createElement("label", { htmlFor: sizeName, key: sizeName, "aria-disabled": !isXsmallButtons, "data-testid": "pdp_fav_size_selector_button", "data-code": variant.code, className: cn(styles.btn, getSizeButtons(), styles.isOutOfStock, outOfStockStyle(), styles.sizeList, 'sizeItem') },
            React.createElement("input", { type: "radio", id: sizeName, name: "sizeLabel", className: styles.buttonInput, disabled: !isXsmallButtons && sizeButtons === 'large' && !isMobile, onChange: () => sizeCode && outOfStockStyling === 'line-through' && handleSizeSelect(sizeCode) }),
            sizeName,
            React.createElement(ScreenReaderOnlyText, null, labels.labelOutOfStock)));
    })));
};
//# sourceMappingURL=SizeButtonGroup.js.map